import React, { useState, useEffect } from "react";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import ModalDialog from "../../shared/ModalDialog";
import Message from "../../../i18n/Message";
import label from "../../../i18n/label";
import Spinner from "../../../assets/icons/Spinner";
import PlusIcon from "../../../assets/icons/PlusIcon";
import Notify from "../../shared/Notify";
import backend from "../../../backend/backend";
import LogoConfluence from "../../../assets/icons/LogoConfluence";

import "./AddIntegrationDialog.scss";

const STEP_SELECT_TYPE = 1;
const STEP_CONFIGURE = 2;

const AddIntegrationDialog = ({ onClose, onSuccess }) => {
  const intl = useIntl();
  const { workspaceId } = useParams();
  const [step, setStep] = useState(STEP_SELECT_TYPE);
  const [selectedType, setSelectedType] = useState(null);
  const [loading, setLoading] = useState(false);

  // Form fields
  const [confluenceUrl, setConfluenceUrl] = useState("");
  const [username, setUsername] = useState("");
  const [apiToken, setApiToken] = useState("");

  const [formElem, setFormElem] = useState(null);

  const handleTypeSelect = (type) => {
    setSelectedType(type);
    setStep(STEP_CONFIGURE);
  };

  const handleBack = () => {
    setStep(STEP_SELECT_TYPE);
    setSelectedType(null);
  };

  const handleFinish = async (e) => {
    e.preventDefault();

    const valid = formElem?.checkValidity();
    if (formElem) formElem.reportValidity();
    if (!valid) return;

    setLoading(true);

    // Auto-generate name: "Confluence - username"
    const integrationName = `${label(intl, "integration_confluence")} - ${username}`;

    const request = {
      name: integrationName,
      type: selectedType,
      baseUrl: confluenceUrl,
      credentials: {
        username: username,
        apiToken: apiToken,
      },
    };

    try {
      const result = await backend.createIntegration(workspaceId, request);
      
      // Test connection automatically
      try {
        await backend.testIntegration(workspaceId, result.uid);
      } catch (testErr) {
        console.warn("Connection test failed:", testErr);
        // Continue even if test fails - connection was created
      }

      Notify.success(label(intl, "integration_created"));
      setLoading(false);
      if (onSuccess) {
        onSuccess(result);
      }
      onClose();
    } catch (err) {
      console.error("Failed to create integration:", err);
      Notify.error(label(intl, "error_create_integration"));
      setLoading(false);
    }
  };

  return (
    <ModalDialog onClose={onClose} className="add_integration_dialog">
      {step === STEP_SELECT_TYPE && (
        <>
          <div className="modal_title">
            <Message id="integration_add" />
          </div>
          <div className="dialog_body">
            <div className="integration_types">
              <div
                className="integration_type_card"
                onClick={() => handleTypeSelect("CONFLUENCE")}
              >
                <div className="type_icon">
                  <LogoConfluence size={48} />
                </div>
                <div className="type_name">
                  <Message id="integration_confluence" />
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {step === STEP_CONFIGURE && selectedType === "CONFLUENCE" && (
        <>
          <div className="modal_title">
            <Message id="integration_create_title" />
          </div>
          <form onSubmit={handleFinish} ref={(val) => setFormElem(val)}>
            <div className="dialog_body">
              <div className="form_group">
                <label>
                  <Message id="integration_confluence_url" />
                </label>
                <input
                  className="form-control"
                  type="url"
                  required
                  placeholder={label(intl, "integration_confluence_url_placeholder")}
                  value={confluenceUrl}
                  onChange={(e) => setConfluenceUrl(e.target.value)}
                  readOnly={loading}
                />
              </div>

              <div className="form_group">
                <label>
                  <Message id="integration_username" />
                </label>
                <input
                  className="form-control"
                  type="text"
                  required
                  placeholder={label(intl, "integration_username_placeholder")}
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  readOnly={loading}
                />
              </div>

              <div className="form_group">
                <label>
                  <Message id="integration_api_token" />
                </label>
                <input
                  className="form-control"
                  type="password"
                  required
                  placeholder={label(intl, "integration_api_token_placeholder")}
                  value={apiToken}
                  onChange={(e) => setApiToken(e.target.value)}
                  readOnly={loading}
                />
              </div>
            </div>

            <div className="buttons">
              <button
                className="btn no-btn"
                type="button"
                onClick={handleBack}
                disabled={loading}
              >
                <Message id="integration_back" />
              </button>
              <button
                className="btn bg-gradient-dark"
                type="submit"
                disabled={loading}
              >
                {loading ? (
                  <Spinner size="18" />
                ) : (
                  <PlusIcon color="#fff" strokeWidth="2" size="18" />
                )}
                <Message id="integration_finish" />
              </button>
            </div>
          </form>
        </>
      )}
    </ModalDialog>
  );
};

export default AddIntegrationDialog;

